import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

export default function ProjectModal({ project, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    if (project) {
      setIsVisible(true);
      setTimeout(() => setContentVisible(true), 50);
    } else {
      setContentVisible(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [project]);

  useEffect(() => {
    if (!project) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [project, onClose]);

  if (!project && !isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-8 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
        onClick={onClose}
      />

      {/* Modal content */}
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto glass border border-white/[0.06] rounded-2xl lg:rounded-3xl shadow-2xl transition-all duration-500 ${
          contentVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent-blue/30 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 group"
        >
          <X className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
        </button>

        {/* Media */}
        <div className="relative">
          {project?.mediaType === 'video' ? (
            <video
              src={project.image}
              className="w-full h-56 lg:h-80 object-cover rounded-t-2xl"
              controls
              controlsList="nodownload nofullscreen noremoteplayback"
              onContextMenu={(e) => e.preventDefault()}
              disablePictureInPicture
              playsInline
            />
          ) : (
            <img
              src={project?.image}
              alt={project?.title}
              className="w-full h-56 lg:h-80 object-cover rounded-t-2xl"
            />
          )}
          {/* Gradient overlay on media */}
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent rounded-t-2xl pointer-events-none" />
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 space-y-6">
          {/* Title and role */}
          <div>
            <h2 className="font-display font-bold text-2xl lg:text-3xl text-white mb-2">
              {project?.title}
            </h2>
            <span className="inline-block text-sm font-medium text-accent-cyan bg-accent-cyan/10 px-3 py-1 rounded-lg">
              {project?.role}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm lg:text-base text-gray-400 leading-relaxed">
            {project?.fullDescription}
          </p>

          {/* Platform */}
          <div className="flex items-center gap-2 text-xs text-gray-500 font-mono">
            <span className="text-accent-blue">◆</span>
            {project?.platform}
          </div>

          {/* Technologies */}
          <div>
            <h3 className="font-display font-bold text-sm text-white mb-3 flex items-center gap-2">
              <div className="w-1 h-4 rounded-full bg-gradient-to-b from-accent-blue to-accent-purple" />
              Tecnologías
            </h3>
            <div className="flex flex-wrap gap-2">
              {project?.technologies.map((tech, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 text-xs rounded-lg bg-white/[0.04] border border-white/[0.06] text-gray-300 hover:text-white hover:border-accent-blue/30 hover:bg-accent-blue/[0.04] transition-all duration-300"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
